import { Inject } from '@odinjs/odin';

import { ButtonAPI, PanelAPI, TextAreaAPI, VirtualAssistantButtonAPI, VirtualAssistantButtonStatus } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnClose, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { TimerAPI } from '@philips-emr/health-professional-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';

import { OpenConsentModalService, STATUS_OF_CONSENT } from './open-consent-modal-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { FragmentNames, RecordingOfConsentAliases } from '../../../codegen/index';
import { ClinicalNotesConsentsRequest } from '../clinical-notes-types';
import { ApplicationRoute } from '../../../constants/routes';
import BaseController from '../../BaseController';
import DialogService from '../../../services/dialog-service';
import ShowToast from '../../../utils/toast-service';
import { RecordingEvent, RecordingState } from './smartcare/smartcare-transcription';
import { SmartCareTranscriptionFactory } from './smartcare/smartcare-transcription-factory';
import SmartcareMetricxTags from './smartcare/smartcare-metricx-tags';

import './recording-of-consent.css'

@Controller({ component: RecordingOfConsentAliases.LAYOUT_RECORDING_OF_CONSENT, fragment: FragmentNames.RECORDING_OF_CONSENT })
export default class RecordingOfConsentController extends BaseController {

  @Inject(RecordingOfConsentAliases.PANEL_DISCARD_CONSENT)
  private panelDiscardConsentApi!: PanelAPI;

  @Inject(RecordingOfConsentAliases.BUTTON_SAVE_RECORDING_OF_CONSENT)
  public buttonSaveRecordingOfConsentApi!: ButtonAPI;

  @Inject(RecordingOfConsentAliases.PANEL_GLOBAL_ACTIONS_RECORDING_OF_CONSENT)
  private panelGlobalActionsRecordingOfConsentApi!: PanelAPI;

  @Inject(RecordingOfConsentAliases.TEXT_TOUCH_TO_START)
  private textTouchToStartApi!: ButtonAPI;

  @Inject(RecordingOfConsentAliases.TEXT_RECORD_STATUS)
  private textRecordStatusApi!: ButtonAPI;

  @Inject(RecordingOfConsentAliases.BUTTON_SHOW_TRANSCRIPT_TEXTS)
  private buttonShowTranscriptTextApi!: ButtonAPI;

  @Inject(RecordingOfConsentAliases.PANEL_TRANSCRIPT_TEXTS)
  private panelTranscriptTextsApi!: PanelAPI;

  @Inject(RecordingOfConsentAliases.TIMER)
  private timerApi!: TimerAPI;

  @Inject(RecordingOfConsentAliases.VA_BUTTON)
  declare virtualAssistantButtonApi: VirtualAssistantButtonAPI;

  private url = this.historyService.getCurrentRouteState().smartCareUrl as string
  private readonly smartcareService = SmartCareTranscriptionFactory.createTranscriptionService(this.url, SmartcareMetricxTags.mountTags());

  @OnMount(RecordingOfConsentAliases.LAYOUT_RECORDING_OF_CONSENT)
  handleMount(): void {
    this.smartcareService.cleanTranscription()
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
  }

  @OnMount(RecordingOfConsentAliases.BUTTON_SAVE_RECORDING_OF_CONSENT)
  handleMountSaveRecordingOfConsentButton({ target }: EventData<ButtonAPI>): void {
    target.setEnabled(!this.smartcareService.transcriptionIsEmpty())
  }

  @OnMount(RecordingOfConsentAliases.LAYOUT_CONTENT_RECORDING)
  handleMountRecordTranscriptConsent(): void {
    const smartCareAudioRecord = this.smartcareService.getAudioRecordInstance();

    smartCareAudioRecord.clearEventListeners()

    smartCareAudioRecord.addEventListener(RecordingEvent.START, () => {
      this.buttonSaveRecordingOfConsentApi.setEnabled(false)
      this.panelGlobalActionsRecordingOfConsentApi.setVisible(false);
    });

    smartCareAudioRecord.addEventListener(RecordingEvent.RESUME, () => {
      this.buttonSaveRecordingOfConsentApi.setEnabled(false)
      this.panelGlobalActionsRecordingOfConsentApi.setVisible(false);
    });
  }

  @OnMount(RecordingOfConsentAliases.TEXT_AREA_TRANSCRIPT_RESULTS)
  handleMountDeck0({ target }: EventData<TextAreaAPI>): void {
    target.setValue(this.smartcareService.getTranscriptionData());
  }

  @OnClick(RecordingOfConsentAliases.BUTTON_DISCARD_RECORDING_OF_CONSENT)
  async handleClickDiscardRecordingOfConsent(): Promise<void> {
    this.smartcareService.pause();
    this.panelDiscardConsentApi.setVisible(true);
  }

  @OnClick(RecordingOfConsentAliases.BUTTON_SAVE_RECORDING_OF_CONSENT)
  async handleClickSaveRecordingOfConsentButton(): Promise<void> {
    this.smartcareService.pause();
    if (this.smartcareService.transcriptionIsEmpty()) {
      ShowToast.open(MessageService.translate('text.transcription-is-necessary'), ToastLevel.WARNING);
      return
    }

    const transcription = this.smartcareService.getTranscriptionData();
    this.openDialogToConfirmSaveRecord(transcription)
  }

  @OnClick(RecordingOfConsentAliases.BUTTON_DISCARD_CONSENT)
  async handleDiscardConsentClick(): Promise<void> {
    this.smartcareService.stop();
    this.virtualAssistantButtonApi.setMediaStream(undefined);
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.SUEP_CLINICAL_NOTES_LIST, { encounterId, patientId });
  }

  @OnClick(RecordingOfConsentAliases.BUTTON_CANCEL_DISCARD_CONSENT)
  handleCancelDiscardConsentClick(): void {
    this.panelDiscardConsentApi.setVisible(false);
  }

  private openDialogToConfirmSaveRecord(transcription?: string): void {
    DialogService.open({
      descriptionDialog: 'text.save-transcription-question-content',
      onClickCancel: () => {
        applicationContext.popNavigationItem();
      },
      onClickOk: () => {
        this.virtualAssistantButtonApi.setMediaStream(undefined);
        this.smartcareService.stop();
        this.saveRecord(transcription);
      },
      titleDialog: 'text.save-transcription-question',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'tws.form.save',
    });
  }

  private saveRecord(transcription?: string): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    const body = {
      encounterId,
      patientId,
      statusConsent: STATUS_OF_CONSENT.ACCEPTED,
      transcription,
    }
    OpenConsentModalService.generateConsent(body as ClinicalNotesConsentsRequest)
      .then(() => {
        ShowToast.open(MessageService.translate('text.consent-registered'), ToastLevel.SUCCESS);
        applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_CONSENT_TRANSCRIPT, { encounterId, patientId }, { state: { smartCareUrl: this.url } });
      });
  }

  @OnClick(RecordingOfConsentAliases.VA_BUTTON)
  async handleRecordClick(): Promise<void> {
    if (this.virtualAssistantButtonApi.getStatus() === VirtualAssistantButtonStatus.PROCESSING) {
      return
    }

    switch (this.smartcareService.getState()) {
      case RecordingState.RECORDING:
        this.pause()
        break;
      case RecordingState.PAUSED:
        this.resumeRecording();
        break;
      default:
        this.startRecording();
        break;
    }
  }

  @OnClick(RecordingOfConsentAliases.BUTTON_SHOW_TRANSCRIPT_TEXTS)
  handleClickShowTrancriptText(): void {
    this.panelTranscriptTextsApi.setVisible(true);
  }

  @OnClick(RecordingOfConsentAliases.BUTTON_CLOSE_TRANSCRIPT_TEXTS)
  handleClickCloseTrancriptText(): void {
    this.panelTranscriptTextsApi.setVisible(false);
  }

  @OnClose(RecordingOfConsentAliases.MODAL_TRANSCRIPT_TEXTS)
  handleCloseModalTranscriptTexts(): void {
    this.panelTranscriptTextsApi.setVisible(false);
  }

  private async startRecording(): Promise<void> {
    this.smartcareService.start()
      .then(() => {
        this.virtualAssistantButtonApi.setMediaStream(this.smartcareService.getStream());
        this.processingTranscription()
      });
  }

  public async pause(): Promise<void> {
    this.virtualAssistantButtonApi.setMediaStream(undefined);
    this.timerApi.stopTimer();
    this.virtualAssistantButtonApi.setStatus(VirtualAssistantButtonStatus.PROCESSING);
    this.textTouchToStartApi.setValue('Por favor, aguarde um momento')
    this.textRecordStatusApi.setValue(MessageService.translate('text.pause'))
    this.buttonShowTranscriptTextApi.setVisible(true)
    this.buttonShowTranscriptTextApi.setLoading(true)
    this.smartcareService.pause()
      .finally(() => {
        this.virtualAssistantButtonApi.setStatus(VirtualAssistantButtonStatus.DEFAULT);
        this.textTouchToStartApi.setValue(MessageService.translate('text.touch-to-return'))
        this.buttonShowTranscriptTextApi.setLoading(false)
        this.panelGlobalActionsRecordingOfConsentApi.setVisible(true);
      });
  }

  private processingTranscription(): void {
    this.timerApi.startTimer();
    this.virtualAssistantButtonApi.setStatus(VirtualAssistantButtonStatus.LISTENING);
    this.textRecordStatusApi.setValue(MessageService.translate('text.listening'))
    this.textTouchToStartApi.setValue(MessageService.translate('text.touch-to-pause'))
    this.buttonShowTranscriptTextApi.setVisible(false)
  }

  private async resumeRecording(): Promise<void> {
    this.virtualAssistantButtonApi.setMediaStream(this.smartcareService.getStream());
    this.smartcareService.resume()
    this.processingTranscription()
  }
}
